/**
 * Flat AND/OR condition editor — Phase 1 step + transition guards.
 *
 * Design constraint (Section 4): "flat AND/OR condition builder, no nested
 * groups". So the editor renders an array of comparison/existence rows
 * combined by a single AND or OR. Nested groups will land in Phase 4.
 *
 * The AST round-trips through ConditionSpec. A null guard renders as an
 * empty "+ Add row" affordance.
 */

import { useMemo } from 'react';
import type {
  AndCondition,
  ComparisonCondition,
  ConditionLiteral,
  ConditionSpec,
  ExistenceCondition,
  OrCondition,
} from '@mana/shared';
import { Button } from '../../ui/Button.js';

type FlatRow = ComparisonCondition | ExistenceCondition;
type FlatOp = 'and' | 'or';

interface FlatGuard {
  op: FlatOp;
  rows: FlatRow[];
}

function isLeafRow(c: ConditionSpec): c is FlatRow {
  return (
    c.op === 'eq' ||
    c.op === 'neq' ||
    c.op === 'gt' ||
    c.op === 'gte' ||
    c.op === 'lt' ||
    c.op === 'lte' ||
    c.op === 'exists' ||
    c.op === 'not_exists'
  );
}

function flatten(c: ConditionSpec | null): FlatGuard {
  if (c === null) return { op: 'and', rows: [] };
  if (c.op === 'and') {
    const rows: FlatRow[] = [];
    for (const child of (c as AndCondition).conditions) {
      if (isLeafRow(child)) rows.push(child);
    }
    return { op: 'and', rows };
  }
  if (c.op === 'or') {
    const rows: FlatRow[] = [];
    for (const child of (c as OrCondition).conditions) {
      if (isLeafRow(child)) rows.push(child);
    }
    return { op: 'or', rows };
  }
  if (isLeafRow(c)) return { op: 'and', rows: [c] };
  // Nested / NOT — Phase 1 doesn't surface in UI. Return empty + warn caller.
  return { op: 'and', rows: [] };
}

function unflatten(g: FlatGuard): ConditionSpec | null {
  if (g.rows.length === 0) return null;
  if (g.rows.length === 1) return g.rows[0]!;
  if (g.op === 'and') return { op: 'and', conditions: g.rows };
  return { op: 'or', conditions: g.rows };
}

export function GuardEditor({
  value,
  onChange,
  readOnly,
}: {
  value: ConditionSpec | null;
  onChange: (next: ConditionSpec | null) => void;
  readOnly: boolean;
}) {
  const flat = useMemo(() => flatten(value), [value]);
  const hasNested = useMemo(() => {
    if (value === null) return false;
    if (isLeafRow(value)) return false;
    if (value.op === 'and' || value.op === 'or') {
      const allLeaves = (value.conditions ?? []).every(isLeafRow);
      return !allLeaves;
    }
    // NOT or other — nested by definition for our flat editor.
    return true;
  }, [value]);

  const updateFlat = (next: FlatGuard) => {
    onChange(unflatten(next));
  };

  if (hasNested) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
        This guard uses a nested condition group. The Phase 1 editor only shows
        flat AND/OR rows — edit the JSON directly or simplify the group to use
        this builder.
      </div>
    );
  }

  if (flat.rows.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-200 p-3 text-xs text-slate-500">
        No guard — step entered whenever the incoming edge fires.
        <div className="mt-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={readOnly}
            onClick={() =>
              updateFlat({
                op: 'and',
                rows: [{ op: 'eq', path: 'loan.lockStatusRaw', value: '1' }],
              })
            }
          >
            + Add condition
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2 rounded-lg border border-slate-200 p-3">
      <div className="flex items-center gap-2 text-xs">
        <span className="font-medium text-slate-500">Combine with</span>
        <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5">
          {(['and', 'or'] as const).map((op) => (
            <button
              key={op}
              type="button"
              disabled={readOnly}
              onClick={() => updateFlat({ ...flat, op })}
              className={`rounded px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider transition-colors ${
                flat.op === op
                  ? 'bg-brand-navy-700 text-white'
                  : 'text-slate-500 hover:bg-slate-50'
              }`}
            >
              {op}
            </button>
          ))}
        </div>
      </div>

      {flat.rows.map((row, idx) => (
        <RowEditor
          key={idx}
          row={row}
          readOnly={readOnly}
          onChange={(next) => {
            const copy = [...flat.rows];
            copy[idx] = next;
            updateFlat({ ...flat, rows: copy });
          }}
          onRemove={() => {
            const copy = flat.rows.filter((_, i) => i !== idx);
            updateFlat({ ...flat, rows: copy });
          }}
        />
      ))}

      <Button
        variant="secondary"
        size="sm"
        disabled={readOnly}
        onClick={() =>
          updateFlat({
            ...flat,
            rows: [...flat.rows, { op: 'eq', path: 'loan.lockStatusRaw', value: '1' }],
          })
        }
      >
        + Add condition
      </Button>
    </div>
  );
}

function RowEditor({
  row,
  onChange,
  onRemove,
  readOnly,
}: {
  row: FlatRow;
  onChange: (next: FlatRow) => void;
  onRemove: () => void;
  readOnly: boolean;
}) {
  const isExistence = row.op === 'exists' || row.op === 'not_exists';
  return (
    <div className="flex items-center gap-2 text-xs">
      <input
        type="text"
        value={row.path}
        onChange={(e) => onChange({ ...row, path: e.target.value })}
        placeholder="loan.lockStatusRaw"
        disabled={readOnly}
        className="w-48 rounded-md border border-slate-300 bg-white px-2 py-1 font-mono text-[11px] focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-200"
      />
      <select
        value={row.op}
        onChange={(e) => {
          const op = e.target.value as FlatRow['op'];
          if (op === 'exists' || op === 'not_exists') {
            onChange({ op, path: row.path });
          } else {
            const v: ConditionLiteral = isExistence ? '' : (row as ComparisonCondition).value;
            onChange({ op, path: row.path, value: v });
          }
        }}
        disabled={readOnly}
        className="rounded-md border border-slate-300 bg-white px-2 py-1 text-[11px] focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-200"
      >
        <option value="eq">==</option>
        <option value="neq">!=</option>
        <option value="gt">&gt;</option>
        <option value="gte">&gt;=</option>
        <option value="lt">&lt;</option>
        <option value="lte">&lt;=</option>
        <option value="exists">exists</option>
        <option value="not_exists">not exists</option>
      </select>
      {!isExistence && (
        <input
          type="text"
          value={String((row as ComparisonCondition).value ?? '')}
          onChange={(e) => {
            const raw = e.target.value;
            const value: ConditionLiteral =
              raw === ''
                ? ''
                : !isNaN(Number(raw)) && raw.trim() !== ''
                  ? Number(raw)
                  : raw;
            onChange({ ...(row as ComparisonCondition), value });
          }}
          disabled={readOnly}
          placeholder="value"
          className="flex-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-[11px] focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-200"
        />
      )}
      <button
        type="button"
        disabled={readOnly}
        onClick={onRemove}
        title="Remove condition"
        className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-rose-600 disabled:cursor-not-allowed"
      >
        ✕
      </button>
    </div>
  );
}
